/**
 * Insurance Renewal Pricing — computation utilities
 *
 * Simplified model: spread insurance investment + interest over the cover period.
 *
 *   F  = Ip − Dc           (Primelane funded)
 *   Rp = Cp × (52/12)      (repayment weeks = cover period in weeks)
 *   totalInterest = F × r × (Rp / 52)
 *   totalRepaid   = F + totalInterest
 *   Wr = totalRepaid / Rp   (renewal weekly charge)
 *
 * Variables:
 *   Cp = cover period (months: 3, 6, 9, or 12)
 *   Ip = insurance premium
 *   Dc = driver contribution
 *   F  = Primelane funded = Ip − Dc
 *   r  = annual interest rate
 *   Rp = repayment period (weeks) = cover period in weeks
 *   Wr = renewal weekly charge
 */

export interface RenewalInputs {
  coverPeriodMonths: number;       // Cp (3, 6, 9, or 12)
  insurancePremium: number;        // Ip
  driverContribution: number;      // Dc
  annualInterestRate: number;      // r — e.g. 0.40 (40%)
}

export interface RenewalOutputs {
  primelaneFunded: number;         // F = Ip − Dc
  repaymentWeeks: number;          // Rp = Cp × (52/12)
  renewalWeekly: number;           // Wr = totalRepaid / Rp
  totalRepaid: number;
  totalInterest: number;
  effectiveAnnualRate: number;     // echo back as %, for display
}

/**
 * Compute renewal pricing outputs.
 */
export function computeRenewal(inputs: RenewalInputs): RenewalOutputs {
  const {
    coverPeriodMonths,
    insurancePremium,
    driverContribution,
    annualInterestRate,
  } = inputs;

  const primelaneFunded = Math.max(0, insurancePremium - driverContribution);
  const repaymentWeeks = coverPeriodMonths * (52 / 12);

  // Nothing to finance
  if (primelaneFunded <= 0 || repaymentWeeks <= 0) {
    return {
      primelaneFunded,
      repaymentWeeks,
      renewalWeekly: 0,
      totalRepaid: 0,
      totalInterest: 0,
      effectiveAnnualRate: annualInterestRate * 100,
    };
  }

  const repaymentYears = repaymentWeeks / 52;
  const totalInterest = primelaneFunded * annualInterestRate * repaymentYears;
  const totalRepaid = primelaneFunded + totalInterest;
  const renewalWeekly = totalRepaid / repaymentWeeks;

  return {
    primelaneFunded,
    repaymentWeeks,
    renewalWeekly,
    totalRepaid,
    totalInterest,
    effectiveAnnualRate: annualInterestRate * 100,
  };
}

/**
 * Default renewal inputs.
 */
export function getDefaultRenewalInputs(): RenewalInputs {
  return {
    coverPeriodMonths: 12,
    insurancePremium: 8000,
    driverContribution: 0,
    annualInterestRate: 0.40,       // 40%
  };
}

/**
 * Map DB key-value rows to RenewalInputs.
 */
export function dbRowsToRenewalInputs(rows: Array<{ key: string; value: string }>): RenewalInputs {
  const defaults = getDefaultRenewalInputs();
  const map = new Map(rows.map((r) => [r.key, r.value]));

  return {
    coverPeriodMonths: parseFloat(map.get('renewal_cover_months') ?? '') || defaults.coverPeriodMonths,
    insurancePremium: parseFloat(map.get('renewal_premium') ?? '') || defaults.insurancePremium,
    driverContribution: parseFloat(map.get('renewal_driver_contribution') ?? '') ?? defaults.driverContribution,
    annualInterestRate: parseFloat(map.get('renewal_annual_rate') ?? '') || defaults.annualInterestRate,
  };
}

/**
 * Convert RenewalInputs to DB rows for upsert.
 */
export function renewalInputsToDbRows(inputs: RenewalInputs): Array<{ key: string; value: string; sort_order: number }> {
  return [
    { key: 'renewal_cover_months', value: String(inputs.coverPeriodMonths), sort_order: 101 },
    { key: 'renewal_premium', value: String(inputs.insurancePremium), sort_order: 102 },
    { key: 'renewal_driver_contribution', value: String(inputs.driverContribution), sort_order: 103 },
    { key: 'renewal_annual_rate', value: String(inputs.annualInterestRate), sort_order: 107 },
  ];
}
